import { useSelector } from "react-redux";
import {
  useGetConfigurationByPlantQuery,
  useGetExistingUserQuery,
  useGetNewUserQuery,
  useGetTotalRevenueQuery
} from "Services/Admin";
import { formatIndianNumber } from "Utilities";
import { RootState } from "State/store";

const InsightRow = ({ icon, bg, label, value, loading }: any) => (
  <div className="py-2.5 border-b border-gray-50 last:border-0 min-h-[64px] flex flex-col justify-center">
    {/* Top Row: Icon + Label */}
    <div className="flex items-center gap-3 mb-1.5">
      <div className={`w-7 h-7 rounded-lg ${bg} flex-shrink-0 flex items-center justify-center text-white text-[12px] font-bold shadow-sm`}>
        {icon}
      </div>
      <span className="text-[12px] xl:text-[13px] font-semibold text-[#1B2559] leading-tight block whitespace-normal">
        {label}
      </span>
    </div>
    
    {/* Bottom Row: Value (Indented to align with label text) */}
    <div className="pl-10">
      {loading ? (
        <div className="w-16 h-4 bg-gray-50 animate-pulse rounded" />
      ) : (
        <span className="text-[15px] xl:text-[18px] font-black text-[#1B2559] leading-none block tabular-nums">
          {value}
        </span>
      )}
    </div>
  </div>
);

export const CustomerInsights = () => {
  const plant = process.env.REACT_APP_PLANT;
  const adminToken = useSelector((state: RootState) => state.adminlogin.adminToken);
  const { data: config } = useGetConfigurationByPlantQuery(plant || "", { skip: !adminToken });
  const dec = config?.numberOfDecimal ? parseInt(config.numberOfDecimal) : 0;
  const { data: nUsr, isLoading: l1 } = useGetNewUserQuery({ plant });
  const { data: eUsr, isLoading: l2 } = useGetExistingUserQuery({ plant });
  const { data: rev, isLoading: l3 } = useGetTotalRevenueQuery({ plant });

  const newUsers = Number(nUsr) || 0;
  const existingUsers = Number(eUsr) || 0;
  const revenue = Number(rev) || 0;

  const total = newUsers + existingUsers;
  const clv = total > 0 ? revenue / total : 0;

  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5 xl:px-6 xl:py-4 h-full flex flex-col">
      <h3 className="text-[14px] xl:text-[15px] font-bold text-[#1B2559] pb-2.5 border-b border-gray-100 mb-1 shrink-0">Customer Insights</h3>
      <InsightRow label="New Customers" value={formatIndianNumber(newUsers)} bg="bg-[#4CAF50]" loading={l1} icon={<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle><line x1="19" y1="8" x2="19" y2="14"></line><line x1="22" y1="11" x2="16" y2="11"></line></svg>} />
      <InsightRow label="Returning Customers" value={formatIndianNumber(existingUsers)} bg="bg-[#FF9800]" loading={l2} icon={<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle></svg>} />
      <InsightRow label="CLV" value={`₹${formatIndianNumber(clv, dec)}`} bg="bg-[#4CAF50]" loading={l3} icon={<span className="font-serif font-bold text-[14px]">₹</span>} />
    </div>
  );
};